import { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { eraseDevice } from "../../commands/mdmCommands";

function profileLooksLikeWifi(profile) {
  const data = String(profile?.MobileConfigData || "");
  if (!data) return false;
  try {
    const decoded = atob(data.replace(/\s+/g, ""));
    return decoded.includes("com.apple.wifi.managed");
  } catch {
    return data.includes("com.apple.wifi.managed");
  }
}

function analyzeWifiClientSide(profile) {
  if (!profile) return null;
  let xml = "";
  try {
    xml = atob(String(profile.MobileConfigData || "").replace(/\s+/g, ""));
  } catch {
    xml = String(profile.MobileConfigData || "");
  }
  if (!xml.includes("com.apple.wifi.managed")) {
    return { hasWifi: false, enterprise: false, label: "kein WLAN" };
  }
  const enterprise = xml.includes("EAPClientConfiguration");
  const hasPkcs12 = xml.includes("com.apple.security.pkcs12");
  const hasRoot = xml.includes("com.apple.security.root") || xml.includes("com.apple.security.pem");
  const refsCert = /PayloadCertificateUUID|PayloadCertificateAnchorUUID/.test(xml);
  if (enterprise && refsCert && !hasPkcs12 && !hasRoot) {
    return { hasWifi: true, enterprise: true, incomplete: true, label: "Enterprise — Zertifikate fehlen im Profil" };
  }
  if (enterprise) {
    return {
      hasWifi: true,
      enterprise: true,
      incomplete: false,
      label: hasPkcs12 || hasRoot ? "Enterprise inkl. Zertifikate" : "Enterprise (ohne PKCS#12 im Profil)",
    };
  }
  return { hasWifi: true, enterprise: false, incomplete: false, label: "PSK / offen" };
}

function EraseDeviceModal({
  visible,
  UDID,
  platform = "ios",
  device = null,
  configProfiles = [],
  hideEraseDeviceModal,
}) {
  const [show, setShow] = useState(false);
  const [returnToService, setReturnToService] = useState(false);
  const [wifiProfileUuid, setWifiProfileUuid] = useState("");
  const [includeMdmProfile, setIncludeMdmProfile] = useState(false);
  const [preserveApps, setPreserveApps] = useState(false);
  const [preserveDataPlan, setPreserveDataPlan] = useState(true);
  const [disallowProximitySetup, setDisallowProximitySetup] = useState(false);
  const [obliterationBehavior, setObliterationBehavior] = useState("Default");
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isMac = platform === "mac" || platform === "macos";
  const supportsRts = !isMac;
  const hasBootstrapToken = Boolean(device?.hasBootstrapToken);

  const wifiProfiles = useMemo(
    () => (configProfiles || []).filter(profileLooksLikeWifi).map((profile) => ({
      profile,
      analysis: analyzeWifiClientSide(profile),
    })),
    [configProfiles],
  );

  const selectedWifi = useMemo(
    () => wifiProfiles.find((row) => (row.profile.PayloadUUID || row.profile.PayloadIdentifier) === wifiProfileUuid),
    [wifiProfiles, wifiProfileUuid],
  );

  const handleClose = () => {
    setShow(false);
    hideEraseDeviceModal();
  };

  useEffect(() => {
    setShow(visible);
    if (visible) {
      setReturnToService(false);
      setWifiProfileUuid("");
      setIncludeMdmProfile(false);
      setPreserveApps(false);
      setPreserveDataPlan(true);
      setDisallowProximitySetup(false);
      setObliterationBehavior("Default");
      setPin("");
      setSubmitting(false);
    }
  }, [visible]);

  async function erase() {
    if (returnToService && supportsRts && !wifiProfileUuid) {
      const ok = window.confirm(
        "Kein WLAN-Profil gewählt. Apple empfiehlt WiFiProfileData, wenn das Gerät nach dem Wipe kein Ethernet/Mobilfunk hat. Trotzdem fortfahren?",
      );
      if (!ok) return;
    }
    if (selectedWifi?.analysis?.incomplete) {
      window.alert(selectedWifi.analysis.label);
      return;
    }
    if (preserveApps && !hasBootstrapToken) {
      window.alert("Kein BootstrapToken gespeichert. App-Preservation ist erst möglich, nachdem das Gerät SetBootstrapToken gesendet hat.");
      return;
    }
    if (isMac && pin && !/^\d{6}$/.test(pin)) {
      window.alert("PIN muss genau 6 Ziffern haben.");
      return;
    }
    setSubmitting(true);
    try {
      await eraseDevice(UDID, {
        returnToService: supportsRts && returnToService,
        wifiProfileUuid: supportsRts && returnToService ? wifiProfileUuid || undefined : undefined,
        includeMdmProfile: supportsRts && returnToService && includeMdmProfile,
        preserveApps: supportsRts && returnToService && preserveApps,
        preserveDataPlan: !isMac ? preserveDataPlan : undefined,
        disallowProximitySetup: !isMac && disallowProximitySetup,
        obliterationBehavior: isMac ? obliterationBehavior : undefined,
        pin: isMac ? pin || undefined : undefined,
      });
      handleClose();
    } catch {
      /* toast kommt aus eraseDevice */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Gerät löschen (Erase)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Das Gerät wird sofort gelöscht. Die Aktion erfordert Administratorrechte.
        </p>

        {!isMac ? (
          <div className="mb-3">
            <label className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={preserveDataPlan}
                onChange={(e) => setPreserveDataPlan(e.target.checked)}
              />
              <span className="form-check-label">eSIM-/Datenplan behalten (PreserveDataPlan)</span>
            </label>
          </div>
        ) : null}

        {!isMac ? (
          <div className="mb-3">
            <label className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                checked={disallowProximitySetup}
                onChange={(e) => setDisallowProximitySetup(e.target.checked)}
              />
              <span className="form-check-label">Proximity Setup beim nächsten Start unterbinden</span>
            </label>
          </div>
        ) : null}

        {isMac ? (
          <>
            <div className="mb-3">
              <label className="form-label">ObliterationBehavior (macOS)</label>
              <select
                className="form-select"
                value={obliterationBehavior}
                onChange={(e) => setObliterationBehavior(e.target.value)}
              >
                <option value="Default">Default — EACS, sonst Obliteration</option>
                <option value="DoNotObliterate">DoNotObliterate — bei Fehler abbrechen</option>
                <option value="ObliterateWithWarning">ObliterateWithWarning</option>
                <option value="Always">Always — immer Obliteration</option>
              </select>
              <div className="form-text">Fallback, wenn „Alle Inhalte & Einstellungen löschen“ (EACS) scheitert.</div>
            </div>
            <div className="mb-3">
              <label className="form-label">Find-My-PIN (6 Ziffern, optional)</label>
              <input
                type="text"
                className="form-control"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="nur bei Bedarf"
              />
            </div>
          </>
        ) : null}

        {supportsRts ? (
          <div className="border rounded p-3 mb-2">
            <label className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                checked={returnToService}
                onChange={(e) => setReturnToService(e.target.checked)}
              />
              <span className="form-check-label fw-semibold">Return to Service</span>
            </label>
            <p className="small text-muted mb-3">
              Nach dem Wipe verbindet sich das Gerät automatisch (WLAN), enrollt erneut und geht möglichst ohne
              Nutzerinteraktion durch den Setup-Assistenten (iOS/iPadOS 17+, tvOS 18+).
              Activation Lock muss vorher deaktiviert sein.
            </p>

            {returnToService ? (
              <>
                <div className="mb-3">
                  <label className="form-label">WLAN-Profil (WiFiProfileData)</label>
                  <select
                    className="form-select"
                    value={wifiProfileUuid}
                    onChange={(e) => setWifiProfileUuid(e.target.value)}
                  >
                    <option value="">Kein Profil — nur sinnvoll mit Ethernet/Mobilfunk</option>
                    {wifiProfiles.map(({ profile, analysis }) => (
                      <option key={profile.PayloadUUID || profile.PayloadIdentifier} value={profile.PayloadUUID || profile.PayloadIdentifier}>
                        {profile.PayloadDisplayName || profile.PayloadIdentifier}
                        {analysis?.label ? ` — ${analysis.label}` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="form-text">
                    Gesamtes Profil wird mitgeschickt (WLAN + PKCS#12 + Stammzertifikate).
                    Enterprise-802.1X braucht Identität und Trust <strong>im selben</strong> Profil.
                    {wifiProfiles.length === 0 ? " Aktuell ist kein WLAN-Profil erkennbar." : null}
                  </div>
                  {selectedWifi?.analysis?.incomplete ? (
                    <div className="text-danger small mt-1">{selectedWifi.analysis.label}</div>
                  ) : null}
                </div>

                <label className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={includeMdmProfile}
                    onChange={(e) => setIncludeMdmProfile(e.target.checked)}
                  />
                  <span className="form-check-label">MDM-Enrollment-Profil mitsenden (MDMProfileData)</span>
                </label>
                <div className="form-text mb-3">
                  Empfohlen ohne ADE bzw. wenn das ADE-Profil eine <code>configuration-web-url</code> hat.
                </div>

                <label className={`form-check ${hasBootstrapToken ? "" : "text-muted"}`}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={preserveApps}
                    disabled={!hasBootstrapToken}
                    onChange={(e) => setPreserveApps(e.target.checked)}
                  />
                  <span className="form-check-label">Apps erhalten (Return to Service mit App-Preservation)</span>
                </label>
                <div className="form-text">
                  {hasBootstrapToken
                    ? "BootstrapToken liegt vor und wird im Erase-Befehl mitgesendet. ADE-Profil sollte „Apps bei Wipe belassen“ / is_return_to_service nutzen."
                    : "Noch kein BootstrapToken gespeichert. Nach ADE-Enrollment sendet das Gerät SetBootstrapToken an NanoMDM; Xavier übernimmt den Webhook mdm.SetBootstrapToken."}
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <p className="small text-muted mb-0">
            Return to Service ist laut Apple nicht für macOS verfügbar.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <button type="button" className="btn btn-danger" onClick={erase} disabled={submitting}>
          {submitting
            ? "Sende…"
            : (returnToService && supportsRts
              ? (preserveApps ? "Löschen & RTS (Apps erhalten)" : "Löschen & Return to Service")
              : "Gerät löschen")}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default EraseDeviceModal;
